import * as React from 'react';
import { OTPInput, OTPInputContext } from 'input-otp';
import { MinusIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

const MaskOTPContext = React.createContext<{ mask?: boolean }>({});

function InputOTP({
  className,
  containerClassName,
  mask,
  ...props
}: React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
  mask?: boolean;
}) {
  return (
    <MaskOTPContext.Provider value={{ mask }}>
      <OTPInput
        data-slot="input-otp"
        containerClassName={cn(
          'flex items-center gap-2 has-disabled:opacity-50',
          containerClassName
        )}
        className={cn('disabled:cursor-not-allowed', className)}
        {...props}
      />
    </MaskOTPContext.Provider>
  );
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="input-otp-group"
      className={cn('flex gap-3 items-center', className)}
      {...props}
    />
  );
}

function InputOTPSlot({
  index,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  index: number;
}) {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { mask } = React.useContext(MaskOTPContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(
        'data-[active=true]:border-ring data-[active=true]:ring-ring/50 data-[active=true]:aria-invalid:ring-destructive/20 dark:data-[active=true]:aria-invalid:ring-destructive/40 aria-invalid:border-destructive data-[active=true]:aria-invalid:border-destructive dark:bg-input/30 border border-input relative flex h-12 w-12 md:min-h-16 md:w-16 lg:h-18 lg:w-18 font-medium items-center justify-center text-3xl md:text-4xl lg:text-5xl shadow-xs transition-all outline-none rounded-md data-[active=true]:z-10 data-[active=true]:ring-[3px]',
        className
      )}
      {...props}
    >
      {mask && char ? '•' : char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink bg-foreground h-4 w-px duration-1000" />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator({ ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="input-otp-separator" role="separator" {...props}>
      <MinusIcon className="text-muted-foreground" />
    </div>
  );
}

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
